<template>
  <div class="view" v-if="canView" v-cloak>
    <GridPanelHeader
      :title="$t('stock_management.invoice_management.title')"
      :grid-ref="$refs[gridRefKey]"
      :show-add="canEdit"
      :show-filter="false"
      @add="handleAddClick"
      :show-refresh="true"
    />
    <DxDataGrid
      key-expr="uuid"
      :ref="gridRefKey"
      :data-source="invoiceManagementDataSource"
      :remote-operations="gridRemoteOperations"
      :column-min-width="80"
      style="min-height: 350px"
      :show-column-lines="true"
      :show-row-lines="false"
      :show-borders="true"
      :column-auto-width="false"
      :allowColumnReordering="true"
      :allowColumnResizing="true"
      :row-alternation-enabled="true"
      :load-panel="{ enabled: true }"
      :element-attr="gridAttributes"
      @exporting="onExporting"
      @editing-start="onEditingStart"
      @option-changed="onOptionChanged"
      @content-ready="onContentReady"
      :row-height="50"
    >
      <DxColumn
        :allowHeaderFiltering="false"
        :allowFiltering="false"
        :allowSorting="false"
        :allowHiding="false"
        :showInColumnChooser="false"
        :fixed="true"
        fixed-position="left"
        :width="40"
        :min-width="40"
        :max-width="40"
        alignment="center"
        cell-template="itemNumberTemplate"
      />
      <template #itemNumberTemplate="{ data }">
        <span v-if="data.data.reception_item_number > 0" :title="$t('stock_management.invoice_management.reception_item_number')">
          {{ data.data.reception_item_number }}
        </span>
      </template>
      <DxColumn
        dataField="department_name"
        :caption="$t('common.unit')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="partner_name"
        :caption="$t('common.partner')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="invoice_number"
        :caption="$t('stock_management.invoice_management.invoice_number')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="date"
        :caption="$t('stock_management.invoice_management.invoice_date')"
        :sortOrder="'desc'"
        :allowHeaderFiltering="false"
        :filter-operations="['=']"
        dataType="date"
        :allowEditing="false"
        format="dd-MM-yyyy"
        :width="140"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="due_date"
        :caption="$t('stock_management.invoice_management.due_date')"
        :allowHeaderFiltering="false"
        :filter-operations="['=']"
        dataType="date"
        format="dd-MM-yyyy"
        :width="140"
        :allowEditing="false"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="payment_date"
        :caption="$t('stock_management.invoice_management.payment_date')"
        :allowHeaderFiltering="false"
        :filter-operations="['=']"
        dataType="date"
        format="dd-MM-yyyy"
        :width="140"
        :allowEditing="false"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="net_value"
        :caption="$t('stock_management.invoice_management.net_value')"
        :dataType="'number'"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
        :allowFiltering="false"
      ></DxColumn>
      <DxColumn
        dataField="gross_value"
        :caption="$t('stock_management.invoice_management.gross_value')"
        :dataType="'number'"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
        :allowFiltering="false"
      ></DxColumn>
      <DxColumn
        dataField="discount"
        :caption="$t('stock_management.invoice_management.discount')"
        :dataType="'number'"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
        :allowFiltering="false"
      ></DxColumn>
      <DxColumn
        :width="70"
        alignment="center"
        :fixed="true"
        fixed-position="right"
        :allow-sorting="false"
        :allow-filtering="false"
        :show-in-column-chooser="false"
        cell-template="actionsCell"
      />
      <template #actionsCell="{ data }">
        <DxActionMenu
          :grid-ref="$refs[gridRefKey]"
          row-key="uuid"
          :row="data.data"
          :can-edit="canEdit"
          :can-delete="canDelete"
          :allow-deleting="allowDeletingRow"
        />
      </template>
      <DxEditing mode="row" :allowUpdating="false" :allowAdding="false" :allowDeleting="false" :confirm-delete="true" :use-icons="true">
        <DxTexts
          :confirm-delete-message="
            $t('common.confirm_delete', {
              title: $t('stock_management.invoice_management.title'),
            })
          "
          :confirm-delete-title="$t('common.confirm_delete_title')"
          :deleteRow="$t('common.delete')"
        />
      </DxEditing>
      <DxPaging :page-size="15" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="pageSizes"
        display-mode="compact"
        :info-text="$t('common.page_info')"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
      />
      <DxHeaderFilter :visible="true" />
      <DxFilterRow :visible="true" :showOperationChooser="true" apply-filter="auto" />
      <DxSelection mode="single" />
      <DxSearchPanel :visible="false" :width="240" />
      <DxGroupPanel :visible="false" :empty-panel-text="$t('common.group_panel_placeholder')" />
      <DxExport :enabled="false" :allow-export-selected-data="false" :fileName="'SmInvoiceManagement'" />
      <DxColumnChooser :enabled="false" mode="select" />
      <DxColumnFixing :enabled="true" />
      <!--<DxToolbar>
        <DxItem name="groupPanel" />
        <DxItem name="columnChooserButton" />
        <DxItem name="exportButton" />
        <DxItem name="searchPanel" />
      </DxToolbar>
      -->
      <DxSummary>
        <!-- Total summary for the whole grid -->
        <DxTotalItem column="net_value" summaryType="sum" valueFormat="#,##0.##" displayFormat="Total: {0}" />
        <DxTotalItem column="gross_value" summaryType="sum" valueFormat="#,##0.##" displayFormat="Total: {0}" />
        <DxTotalItem column="discount" summaryType="sum" valueFormat="#,##0.##" displayFormat="Total: {0}" />

        <!-- Group summary for each group -->
        <DxGroupItem column="net_value" summaryType="sum" valueFormat="#,##0.##" displayFormat="Group Total: {0}" />
        <DxGroupItem column="gross_value" summaryType="sum" valueFormat="#,##0.##" displayFormat="Group Total: {0}" />
        <DxGroupItem column="discount" summaryType="sum" valueFormat="#,##0.##" displayFormat="Group Total: {0}" />
      </DxSummary>
    </DxDataGrid>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DxDataGrid, {
  DxColumn,
  DxPaging,
  DxPager,
  DxEditing,
  DxHeaderFilter,
  DxFilterRow,
  DxSelection,
  DxTexts,
  DxButton,
  DxPopup,
  DxToolbar,
  DxItem,
  DxSearchPanel,
  DxExport,
  DxGroupPanel,
  DxColumnChooser,
  DxColumnFixing,
  DxLookup,
  DxSummary,
  DxTotalItem,
  DxGroupItem,
} from "devextreme-vue/data-grid";
import Forbidden from "~/pages/errors/Forbidden.vue";
import CustomStore from "devextreme/data/custom_store";
import DataSource from "devextreme/data/data_source";
import { canView, canDelete, canEdit, permissions, permissionTypes } from "~/helpers/Permissions";
import { format } from "date-fns";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import notify from "devextreme/ui/notify";
import { errorMessage } from "@/helpers/common";
import { saveDxGridColumnSettings, loadDxGridColumnSettings } from "@/helpers/dxGridHelper";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import DxActionMenu from "@/components/DxGrid/DxActionMenu.vue";

export default {
  name: "InvoiceManagement",
  components: {
    DxDataGrid,
    DxColumn,
    DxEditing,
    DxHeaderFilter,
    DxFilterRow,
    DxSelection,
    DxTexts,
    DxButton,
    DxPaging,
    DxPager,
    DxPopup,
    DxToolbar,
    DxItem,
    Forbidden,
    DxSearchPanel,
    DxExport,
    DxGroupPanel,
    DxColumnChooser,
    DxColumnFixing,
    DxLookup,
    DxSummary,
    DxTotalItem,
    DxGroupItem,
    GridPanelHeader,
    DxActionMenu,
  },
  data() {
    return {
      gridAttributes: {
        class: "smagria-grid invoice-management-grid",
      },
      gridRefKey: "invoiceManagementGrid",
      invoiceManagementDataSource: new DataSource({
        store: new CustomStore({
          key: "uuid",
          load: async (loadOptions) => {
            let dataSource = {
              data: [],
              totalCount: 0,
            };

            // not load table data before columnsettings and other page settings  not loaded yet
            if (this.visibilitySettingsLoading || !this.visibilitySettingsLoaded) {
              return dataSource;
            }

            let pageLimit = loadOptions.isLoadingAll ? 99999 : loadOptions.take;
            let pageNumber = (loadOptions.skip + loadOptions.take) / pageLimit;

            let payload = {
              page: pageNumber,
              limit: pageLimit,
              filter: loadOptions.filter,
              sort: loadOptions.sort,
            };

            try {
              await this.getSmInvoiceManagements(payload).then((response) => {
                dataSource.data = response.data.data;
                dataSource.totalCount = response.data?.pagination?.total ?? 1;

                if (response.data?.summary && Array.isArray(response.data.summary)) {
                  dataSource.summary = response.data.summary;
                } else if (Array.isArray(dataSource.data)) {
                  const totals = [0, 0, 0];
                  dataSource.data.forEach((row) => {
                    totals[0] += Number(row?.net_value) || 0;
                    totals[1] += Number(row?.gross_value) || 0;
                    totals[2] += Number(row?.discount) || 0;
                  });
                  dataSource.summary = totals;
                }
              });

              return dataSource;
            } catch (error) {
              console.error("Error fetching invoice managements:", error);
              throw error;
            }
          },
          remove: this.removeSmInvoiceManagement,
        }),
      }),
      gridRemoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
        summary: true, // if not true tha pagination will not work properly
      },
      pageSizes: [15, 25, 100],
      editingRowData: null,
      yes_no_list: [
        {
          id: 1,
          label: this.$t("common.yes"),
        },
        {
          id: 0,
          label: this.$t("common.no"),
        },
      ],
      visibilitySettingsLoaded: false,
      visibilitySettingsLoading: false,
      saveColumnSettingsTimeout: null,
    };
  },
  watch: {},
  computed: {
    ...mapGetters({
      users: "user/users",
    }),
    config() {
      return {
        permission: permissions.SM_INVOICE_MANAGEMENT,
        permission_type: permissionTypes.access,
      };
    },
    canView() {
      return canView(this);
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
    dataGridInstance: function () {
      const grid = this.$refs && this.$refs[this.gridRefKey] ? this.$refs[this.gridRefKey] : null;
      return grid && grid.instance ? grid.instance : null;
    },
  },
  methods: {
    ...mapActions({
      getSmInvoiceManagements: "smInvoiceManagement/getSmInvoices",
      deleteSmInvoiceManagement: "smInvoiceManagement/deleteSmInvoice",
      getDxGridSettings: "dxgrid/getDxGridSettings",
      setDxGridSettings: "dxgrid/setDxGridSettings",
    }),
    handleAddClick(e) {
      this.editingRowData = null;
      this.$router.push({
        name: "invoice-management-edit",
        params: {
          uuid: "add",
        },
      });
    },
    allowUpdating(component) {
      // Allow editing if the row is not deleted and has no stock movement
      return this.canEdit && !component.row.data.deleted_at;
    },
    allowDeleting(component) {
      // Hide delete icon if deleted_at is set
      return this.canDelete && !component.row.data.deleted_at;
    },
    allowDeletingRow(row) {
      return this.canDelete && row && !row.deleted_at;
    },
    onEditingStart(e) {
      e.cancel = true; // Cancel editing for this row
      this.editingRowData = e.data; // Store the row data for editing

      if (e.data.deleted_at) {
        errorMessage(this, this.$t("stock_management.invoice_management.error_edit_deleted"));
        return;
      }

      this.$router.push({
        name: "invoice-management-edit",
        params: {
          uuid: e.data.uuid,
        },
      });
    },
    async removeSmInvoiceManagement(key) {
      try {
        await this.deleteSmInvoiceManagement({ uuid: key }).then((response) => {
          this.notifyMsg(response);
        });
      } catch (error) {
        console.error("Error deleting invoice management:", error);
        throw error;
      }
    },
    onExporting(e) {
      e.fileName = "SmInvoiceManagement_" + format(new Date(), "yyyy_MM_dd_hh_mm_ss");

      if (e.format === "xlsx") {
        const fields = ["deleted_at"];
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("SmInvoiceManagement");
        exportDataGrid({
          component: e.component,
          worksheet,
          autoFilterEnabled: true,
          customizeCell: ({ gridCell, excelCell }) => {
            if (fields.includes(gridCell?.column?.dataField) && excelCell.row > 1) {
              excelCell.value = this.getYesNoColumnText(!!gridCell.data[gridCell?.column?.dataField]);
            }
          },
        }).then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: "application/octet-stream" }), e.fileName + ".xlsx");
          });
        });
      }
    },
    notifyMsg(response) {
      notify({
        message: response.message ?? "",
        type: response.status == "ok" ? "success" : "error",
        displayTime: 2500,
        width: "auto",
        position: { at: "top center", my: "top center" },
      });
    },
    getYesNoColumnText(value) {
      if (value === null) {
        return "";
      }
      return this.yes_no_list.find((item) => item.id == value)?.label;
    },
    getCreatedByColumnText(value) {
      if (!value) {
        return "";
      }
      const user = this.users.find((user) => user.uuid === value);
      return user ? user.name : this.$t("common.unknown_user");
    },
    onOptionChanged(e) {
      if (e && e.fullName === "paging.pageSize" && e.value !== e.previousValue) {
        if (this.dataGridInstance) {
          this.dataGridInstance.option("paging.pageIndex", 0);
          this.dataGridInstance.getDataSource()?.reload?.();
        }
      }

      saveDxGridColumnSettings(this, e);
    },
    onContentReady(e) {
      loadDxGridColumnSettings(this);
    },
  },
  mounted() {},
};
</script>
